import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { physioStatusApi } from '../services/api';
import { Card } from '../components/Common';

export function PhysioLookup() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const { data: physioNames, isLoading } = useQuery({
    queryKey: ['physioNames'],
    queryFn: async () => {
      const response = await physioStatusApi.getPhysioNames();
      return response.data;
    },
  });

  const lookupMutation = useMutation({
    mutationFn: (id: number) => physioStatusApi.lookupPhysio(id),
    onSuccess: (response) => {
      // Redirect to their status page
      navigate(`/status/${response.data.status_token}`);
    },
    onError: () => {
      setError('Failed to find your status page. Please try again.');
    },
  });

  const handleSelect = (id: number) => {
    setError(null);
    lookupMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto space-y-6">
        <Card>
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold text-gray-900">Update Your Status</h1>
            <p className="text-gray-600">
              Select your name below to update your availability.
            </p>
          </div>
        </Card>

        <Card>
          <div className="space-y-3">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
                {error}
              </div>
            )}

            {!physioNames || physioNames.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No physios found.</p>
            ) : (
              <div className="grid gap-2">
                {physioNames.map((physio) => (
                  <button
                    key={physio.id}
                    onClick={() => handleSelect(physio.id)}
                    disabled={lookupMutation.isPending}
                    className={`w-full p-4 text-left rounded-lg border-2 border-gray-200 bg-white
                      hover:border-primary-400 hover:bg-primary-50 transition-all
                      ${lookupMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="font-medium text-gray-900">{physio.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>

        <p className="text-center text-sm text-gray-500">
          Tap your name to update your status to Active, Sick, or Unavailable.
        </p>
      </div>
    </div>
  );
}
